import { Head, Link } from "@inertiajs/react"
import { Building2, Calendar, CreditCard } from "lucide-react"
import { useState } from "react"

import { AccountStateBadge } from "@/components/accounts/account-state-badge"
import type { VoiceOption } from "@/components/agents/setup/types"
import type { BusinessProfilePayload } from "@/components/onboarding/types"
import { AccountDeleteMenu } from "@/components/partner-accounts/account-delete-menu"
import { BillingTab } from "@/components/partner-accounts/billing-tab"
import {
  type AssessmentRecord,
  type LandingPageData,
  type LandingPageSession,
  type LandingPageStats,
  MarketingTab,
  type ProvisionalAgentData,
} from "@/components/partner-accounts/marketing-tab"
import type {
  AccountAgent,
  AccountUser,
  BillingData,
} from "@/components/partner-accounts/types"
import { UsersTab } from "@/components/partner-accounts/users-tab"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import PartnerLayout from "@/layouts/partner-layout"
import { formatCurrency } from "@/lib/utils"
import { editPartnerAccountPath } from "@/routes"

interface Account {
  uid: string
  name: string
  industry: string | null
  state: string
  mrr_cents: number
  agent_count: number
  last_activity_at: string | null
  created_at: string
  logo_url: string | null
  deletable?: boolean
  // IMP-404: false once the account has an active (accepted) user.
  can_invite_user: boolean
}

interface ShowProps {
  account: Account
  agents: AccountAgent[]
  users: AccountUser[]
  billing: BillingData
  // Marketing tab
  landing_page: LandingPageData | null
  landing_page_generating: boolean
  business_profile: BusinessProfilePayload | null
  voices: VoiceOption[]
  provisional_agent: ProvisionalAgentData | null
  landing_page_stats: LandingPageStats | null
  sessions: LandingPageSession[]
  assessment: AssessmentRecord | null
}

// IMP-508: tabs are URL-addressable (?tab=marketing) so other pages can
// deep-link straight to one. IMP-511 retired the Agents tab — the jobs list
// lives on Billing now.
const TAB_VALUES = ["billing", "users", "marketing"]
const DEFAULT_TAB = "billing"

function initialTab() {
  const requested = new URLSearchParams(window.location.search).get("tab")
  return requested && TAB_VALUES.includes(requested) ? requested : DEFAULT_TAB
}

export default function AccountShow({
  account,
  agents,
  users,
  billing,
  landing_page,
  landing_page_generating,
  business_profile,
  voices,
  provisional_agent,
  landing_page_stats,
  sessions,
  assessment,
}: ShowProps) {
  const [tab, setTab] = useState(initialTab)

  function handleTabChange(value: string) {
    setTab(value)
    // Reflected without an Inertia visit: switching tabs shouldn't refetch,
    // just leave a copyable/deep-linkable URL behind.
    const url = new URL(window.location.href)
    if (value === DEFAULT_TAB) url.searchParams.delete("tab")
    else url.searchParams.set("tab", value)
    window.history.replaceState(window.history.state, "", url)
  }

  const joinDate = new Date(account.created_at).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  })

  return (
    <PartnerLayout>
      <Head title={account.name} />
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-4">
            <h1 className="text-2xl/8 font-semibold">{account.name}</h1>
            <AccountStateBadge state={account.state} />
          </div>
          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
            <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
              {account.industry && (
                <span className="flex items-center gap-3 text-sm/6">
                  <Building2 className="text-muted-foreground size-4 shrink-0" />
                  {account.industry}
                </span>
              )}
              <span className="flex items-center gap-3 text-sm/6">
                <CreditCard className="text-muted-foreground size-4 shrink-0" />
                {formatCurrency((account.mrr_cents ?? 0) / 100)}/mo
              </span>
              <span className="flex items-center gap-3 text-sm/6">
                <Calendar className="text-muted-foreground size-4 shrink-0" />
                Since {joinDate}
              </span>
            </div>
            <div className="flex gap-4">
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                asChild
              >
                <Link href={editPartnerAccountPath({ id: account.uid })}>
                  Edit
                </Link>
              </Button>
              <AccountDeleteMenu account={account} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={handleTabChange}>
          <div className="border-b">
            <TabsList variant="line">
              <TabsTrigger value="billing">Billing</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="marketing">Marketing</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="billing" className="mt-6">
            <BillingTab
              billing={billing}
              agents={agents ?? []}
              accountUid={account.uid}
            />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <UsersTab
              users={users}
              accountUid={account.uid}
              canInvite={account.can_invite_user}
            />
          </TabsContent>

          <TabsContent value="marketing" className="mt-6">
            <MarketingTab
              landingPage={landing_page ?? null}
              generating={landing_page_generating ?? false}
              businessProfile={business_profile ?? null}
              voices={voices ?? []}
              provisionalAgent={provisional_agent ?? null}
              stats={landing_page_stats ?? null}
              sessions={sessions ?? []}
              assessment={assessment ?? null}
            />
          </TabsContent>
        </Tabs>
      </div>
    </PartnerLayout>
  )
}
